import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Loader2, Film, ArrowRight, Sparkles, FileText } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import confetti from 'canvas-confetti';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PurchaseSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [status, setStatus] = useState('verifying');
  const [paymentDetails, setPaymentDetails] = useState(null);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    const verifyPayment = async () => {
      if (!sessionId) {
        setStatus('error');
        return;
      }

      try {
        const token = localStorage.getItem('moviebot_token');
        const response = await fetch(`${API_URL}/api/payments/onetime-status/${sessionId}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (response.ok) {
          const data = await response.json();
          setPaymentDetails(data);
          
          if (data.payment_status === 'paid') {
            setStatus('success');
            // Trigger confetti
            confetti({
              particleCount: 100,
              spread: 70,
              origin: { y: 0.6 }
            });
            // Refresh user to get updated script passes
            if (refreshUser) {
              await refreshUser();
            }
          } else {
            setStatus('pending');
          }
        } else {
          setStatus('error');
        }
      } catch (error) {
        console.error('Error verifying payment:', error);
        setStatus('error');
      }
    };

    verifyPayment();
  }, [sessionId, refreshUser]);

  return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center p-6">
      <div className="max-w-lg w-full">
        {status === 'verifying' && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="text-center"
          >
            <Loader2 className="w-16 h-16 text-primary mx-auto mb-6 animate-spin" />
            <h1 className="text-2xl font-bold text-white mb-2">Verifying Your Purchase...</h1>
            <p className="text-muted-foreground">Please wait while we confirm your payment.</p>
          </motion.div>
        )}

        {status === 'success' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", delay: 0.2 }}
              className="w-24 h-24 bg-emerald-500/20 rounded-full flex items-center justify-center mx-auto mb-6"
            >
              <CheckCircle className="w-12 h-12 text-emerald-400" />
            </motion.div>
            
            <h1 className="text-3xl font-black text-white mb-2">You&apos;re All Set!</h1>
            <p className="text-lg text-muted-foreground mb-8">
              Your Script Pass has been activated. Time to finish your screenplay!
            </p>

            <div className="bg-secondary/30 border border-white/10 p-6 mb-8 text-left">
              <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-emerald-400" />
                What&apos;s Now Available
              </h3>
              <ul className="space-y-3">
                {[
                  'Full 3-Act structure unlocked',
                  'All 15 story beats accessible',
                  'Unlimited scenes for your project',
                  'Script export in Fountain format'
                ].map((item, idx) => (
                  <li key={idx} className="flex items-center gap-2 text-muted-foreground">
                    <CheckCircle className="w-4 h-4 text-emerald-400" />
                    {item}
                  </li>
                ))}
              </ul>
            </div>

            <div className="space-y-3">
              <Button
                onClick={() => navigate('/dashboard')}
                className="w-full bg-emerald-500 hover:bg-emerald-400 text-black font-bold py-6"
                data-testid="purchase-success-dashboard-btn"
              >
                <Film className="w-5 h-5 mr-2" />
                Go to Dashboard
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>

              <Link to="/subscription">
                <Button
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10"
                >
                  View All Plans
                </Button>
              </Link>
            </div>
          </motion.div>
        )}

        {status === 'pending' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="w-24 h-24 bg-yellow-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <Loader2 className="w-12 h-12 text-yellow-400 animate-spin" />
            </div>
            
            <h1 className="text-3xl font-black text-white mb-2">Payment Processing</h1>
            <p className="text-lg text-muted-foreground mb-8">
              Your payment is being processed. This usually takes just a moment.
            </p>

            <Button
              onClick={() => window.location.reload()}
              variant="outline"
              className="border-white/20 text-white hover:bg-white/10"
            >
              Check Again
            </Button>
          </motion.div>
        )}

        {status === 'error' && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center"
          >
            <div className="w-24 h-24 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <FileText className="w-12 h-12 text-red-400" />
            </div>
            
            <h1 className="text-3xl font-black text-white mb-2">Something Went Wrong</h1>
            <p className="text-lg text-muted-foreground mb-8">
              We couldn&apos;t verify your payment. If you were charged, please contact support.
            </p>

            <div className="space-y-3">
              <Button
                onClick={() => navigate('/subscription')}
                className="w-full bg-primary hover:bg-primary/90 text-black font-bold"
              >
                Try Again
              </Button>
              
              <Link to="/contact">
                <Button
                  variant="outline"
                  className="w-full border-white/20 text-white hover:bg-white/10"
                >
                  Contact Support
                </Button>
              </Link>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

export default PurchaseSuccess;
